import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState
} from 'react';
import {
  TextField,
  FormControlLabel,
  Checkbox,
  Card,
  Typography
} from '@mui/material';
import {useFieldArray, Controller, useForm} from 'react-hook-form';
import * as yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {useOnlineContext} from './OnlineTestContext';

interface DescriptiveQuestionTypeProps {
  question?: string;
  // eslint-disable-next-line react/boolean-prop-naming
  enforceWordLimit?: boolean;
  wordLimit?: number | undefined;
  setEnforceWordLimit?: (value: boolean) => void;
  setQuestion?: (value: string) => void;
  setWordLimit?: (value: number) => void;
  type?: string;
  handleEditQuestion?:any;
}
interface ChildFormMethods {
  submitForm: () => Promise<any>;
}
const schema = yup.object().shape({
  QuestionText: yup.string().required('Question is required'),
  descriptive_enforce_word_limit_length: yup
    .number()
    .nullable()
    .test(
      'enforce-limit',
      'Word limit is required and cannot exceed 5000',
      function (value) {
        const {descriptive_enforce_word_limit} = this.parent;
        if (descriptive_enforce_word_limit) {
          return value !== null && value !== undefined && value <= 5000;
        }
        return true;
      }
    ),
  descriptive_enforce_word_limit: yup.boolean()
});
const DescriptiveOnlineTest = forwardRef<
  ChildFormMethods,
  DescriptiveQuestionTypeProps
>(({question,handleEditQuestion}, ref) => {
  const {onlineData, setOnlineData} = useOnlineContext();
  const {
    control,
    handleSubmit,
    watch,
    clearErrors,
    setValue,
    trigger,
    getValues,
    formState: {errors}
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      QuestionText: handleEditQuestion?.QuestionText || '',
      descriptive_enforce_word_limit:
      handleEditQuestion?.descriptive_enforce_word_limit || false,
      descriptive_enforce_word_limit_length:
      handleEditQuestion?.descriptive_enforce_word_limit_length || null
    }
  });
  const browserTerminate = watch('descriptive_enforce_word_limit');
  useEffect(() => {
    if (!browserTerminate) {
      clearErrors(['descriptive_enforce_word_limit_length']);
      setValue('descriptive_enforce_word_limit_length', null);
    } else {
      setValue(
        'descriptive_enforce_word_limit_length',
        handleEditQuestion?.descriptive_enforce_word_limit_length || null
      );
    }
  }, [browserTerminate, setValue]);
  useImperativeHandle(ref, () => ({
    submitForm: () =>
      new Promise((resolve, reject) => {
        handleSubmit(data => {
          resolve(data); // Resolve the promise with form data
        })();
      })
  }));
  return (
    <Card variant="outlined" sx={{mx: 'auto', padding: '1vh'}}>
      <div>
        <Typography variant="h1" sx={{mt: 2, mb: 1}}>
          Question
        </Typography>
        <Controller
          name="QuestionText"
          control={control}
          render={({field}) => (
            <TextField
              {...field}
              fullWidth
              label="Write Question Here"
              variant="outlined"
              error={!!errors['QuestionText']}
              helperText={errors['QuestionText']?.message}
            />
          )}
        />
      </div>
      <Controller
        name="descriptive_enforce_word_limit"
        control={control}
        render={({field}) => (
          <FormControlLabel
            control={
              <Checkbox
                checked={field.value ?? false} // Ensure a boolean value is always used
                onChange={e => field.onChange(e.target.checked)}
              />
            }
            label="Enforce word limit"
            sx={{marginTop: 2, marginLeft: '1vh'}}
          />
        )}
      />

      {watch('descriptive_enforce_word_limit') && (
        <div style={{marginTop: '1vh', display: 'flex'}}>
          <Controller
            name="descriptive_enforce_word_limit_length"
            control={control}
            render={({field}) => (
              <TextField
                {...field}
                type="number"
                label="Write Question Here"
                variant="outlined"
                error={!!errors['descriptive_enforce_word_limit_length']}
                helperText={
                  errors['descriptive_enforce_word_limit_length']?.message
                }
                sx={{mb: 2}}
              />
            )}
          />
          <Typography sx={{mt: 2, mb: 1, ml: 1}} style={{fontSize: '0.875rem'}}>
            Enforced Word Limit : 5000
          </Typography>
        </div>
      )}
    </Card>
  );
});

export default DescriptiveOnlineTest;
